import defaults from 'lodash/defaults';
import has from 'lodash/has';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import * as T from 'prop-types';
import {createEventHandler} from 'recompose';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/interval';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/last';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/pairwise';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import fuse from '../../_libs/redux-fusion';
import {getUserId} from '../../auth/reducers';
import {ViewMode, ViewModePropType} from '../enums/ViewMode';
import {buildInit$} from '../rx-utils';


export const filterPropsTypes = {
  onFilterChange: T.func.isRequired,
};

export const filterOptionsPropsTypes = {
  onFilterOptionsUpdate: T.func.isRequired,
  onSortOptionsUpdate: T.func.isRequired,
  filterModalOptions: T.object.isRequired,

  ...filterPropsTypes,
};

export const listPropsTypes = {
  mode: ViewModePropType.isRequired,
  list: T.array.isRequired,
  userId: T.string.isRequired,
  init: T.bool,
  onLoadMore: T.func.isRequired,
};

export const listDefaultProps = {
  mode: ViewMode.LOADING,
  list: [],
  init: true,
};

/**
 * Exposed for unit testing.
 *
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @param {Observable} state$
 * @param {Function} dispatch
 * @param {Observable} props$
 * @private
 */
export function ReduxListFilterStreamedComponent(reduxList, options, state$, dispatch, props$) {
  // Validate arguments
  if (!isObject(reduxList)
    || !isObject(reduxList.actions)
    || !isObject(reduxList.actions.initList)
    || !isString(reduxList.actions.initList.TYPE)
  ) {
    throw new Error('ReduxListComponent missing required reduxList');
  }

  /** @type {ReduxObservableListActions} */
  const actions = reduxList.actions;
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  const filterModalOptions$ = buildFilterModalOptions$(state$, options, reduxListOptions);
  const filterData$ = buildFilterData$(state$, reduxListOptions);

  const onFilterChange = createAutoFilterEvent(actions, reduxListOptions, dispatch, props$, filterData$);
  const onFilterOptionsUpdate = createFilterOptionsEvent(actions, reduxListOptions, dispatch, props$, filterData$);
  const onSortOptionsUpdate = createSortOptionsEvent(actions, reduxListOptions, dispatch, props$, filterData$);

  return props$
    .combineLatest(
      filterModalOptions$,
      (props, filterModalOptions) => ({
        ...props,
        searchOnly: options.searchOnly,
        filterModalOptions,
        onFilterChange,
        onFilterOptionsUpdate,
        onSortOptionsUpdate,
      })
    );
}

/**
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @param {Observable} state$
 * @param {Function} dispatch
 * @param {Observable} props$
 */
export function createFilteredReduxList(reduxList, options, state$, dispatch, props$) {
  /** @type {ReduxObservableListActions} */
  const actions = reduxList.actions;
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  const filterData$ = buildFilterData$(state$, reduxListOptions);

  const onFilterChange = createAutoFilterEvent(actions, reduxListOptions, dispatch, props$, filterData$);

  const {
    onLoadMore,
    onRetryLoad,
    listProps$,
  } = createReduxList(reduxList, options, state$, dispatch, props$);

  return {
    onFilterChange,
    onLoadMore,
    onRetryLoad,
    filterData$,
    listProps$,
  };
}

/**
 * Exposed for unit testing.
 *
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @param {Observable} state$
 * @param {Function} dispatch
 * @param {Observable} props$
 * @private
 */
export function createReduxList(reduxList, options, state$, dispatch, props$) {
  // Validate arguments
  if (
    !isObject(reduxList)
    || !isObject(reduxList.actions)
    || !isObject(reduxList.actions.initList)
    || !isString(reduxList.actions.initList.TYPE)
  ) {
    throw new Error('ReduxListComponent missing required reduxList');
  }

  const leave$ = props$
    .last()
    .shareReplay(1);

  /** @type {ReduxObservableListActions} */
  const actions = reduxList.actions;
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  const {
    handler: onLoadMore,
    stream: loadMore$,
  } = createEventHandler();

  const {
    handler: onRetryLoad,
    stream: retryLoad$,
  } = createEventHandler();

  if (options.autoInit) {
    const initList$ = buildInit$(props$);

    const loadList$ = Observable
      .combineLatest(
        props$.take(1),
        initList$,
        () => null
      )
      .switchMap(() => initData(state$, props$, reduxList, options));

    const destroyList$ = initList$
      .switchMap(() =>
        props$
          .last()
          .map(props => actions.destroyList({}, {parentId: props.parentId}))
      );

    loadList$
      .takeUntil(leave$)
      .subscribe(action => dispatch(action));

    destroyList$
      .takeUntil(leave$.delay(1))
      .subscribe(action => dispatch(action));
  }

  const userId$ = state$
    .map(getUserId)
    .distinctUntilChanged();
  const isLoadingPage$ = stateProp$('isLoadingPage', state$, reduxListOptions);
  const err$ = stateProp$('err', state$, reduxListOptions);
  const hasMore$ = buildHasMore$(state$, options, reduxListOptions);
  const filterData$ = buildFilterData$(state$, reduxListOptions);

  // Retrieve these all props at once so that simultaneous changes are not throttled
  const listProps$ = state$
    .map(rootState => reduxListOptions.getState(rootState))
    .distinctUntilChanged(compareListStateMeta)
    .map(state => ({
      hasMore: hasMoreFromState(state.isFiltered, state.filteredHasMore, state.hasMore),
      list: options.listTransform(reduxListOptions.listToArray(reduxListOptions, state)),
      mode: state.mode,
      total: state.total,
    }))
    .throttleTime(500, void 0, {leading: true, trailing: true});

  loadMore$
    .withLatestFrom(
      props$,
      isLoadingPage$,
      err$,
      hasMore$,
      filterData$,
    )
    .takeUntil(leave$)
    .subscribe(([e, props, isLoadingPage, err, hasMore, filterData]) => {
      const {init, parentId} = props;
      if (init && !isLoadingPage && !err && hasMore) {
        dispatch(actions.loadPage(filterData, {parentId}));
      }
    });

  retryLoad$
    .withLatestFrom(props$, isLoadingPage$, filterData$)
    .takeUntil(leave$)
    .subscribe(([e, props, isLoadingPage, filterData]) => {
      const {parentId} = props;
      if (!isLoadingPage) {
        // TODO: we may want a different action here, or different payload to differentiate the page number retry
        // TODO: move currentPage to state, not the epic
        dispatch(actions.loadPage(filterData, {parentId}));
      }
    });

  return {
    onLoadMore,
    onRetryLoad,
    listProps$: props$
      .combineLatest(
        userId$,
        listProps$,
        (props, userId, listProps) => ({
          userId,
          list: listProps.list,
          mode: showPlaceholder(listProps.list, listProps.mode) ? ViewMode.NO_DATA : listProps.mode,
          hasMore: listProps.mode !== ViewMode.LOADING && listProps.hasMore,
          total: listProps.total,
        })
      ),
  };
}

/**
 * Exposed for unit testing.
 *
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @param {Observable} state$
 * @param {Function} dispatch
 * @param {Observable} props$
 * @private
 */
export function ReduxListStreamedComponent(reduxList, options, state$, dispatch, props$) {
  const {
    onLoadMore,
    onRetryLoad,
    listProps$,
  } = createReduxList(reduxList, options, state$, dispatch, props$);

  return props$
    .combineLatest(
      listProps$,
      (props, listProps) => ({
        ...props,
        ...listProps,
        onLoadMore,
        onRetryLoad,
      })
    );
}

const FilterStreamedComponent = (reduxList, options) => (state$, dispatch) => (props$) => {
  return ReduxListFilterStreamedComponent(reduxList, options, state$, dispatch, props$);
};

/**
 * @param WrappedComponent
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 */
export const ReduxListFilterStreamedContainer = (WrappedComponent, reduxList, options) => {
  const container = FilterStreamedComponent(reduxList, options);
  const fuseContainer = fuse(container)(WrappedComponent);

  const propTypes = {};
  if (options.searchOnly) {
    defaults(propTypes, WrappedComponent.propTypes || {}, filterPropsTypes);
  } else {
    defaults(propTypes, WrappedComponent.propTypes || {}, filterOptionsPropsTypes);
  }
  WrappedComponent.propTypes = propTypes;

  fuseContainer.displayName = 'ReduxListFilterStreamedContainer';

  return fuseContainer;
};

const StreamedComponent = (reduxList, options) => (state$, dispatch) => (props$) => {
  return ReduxListStreamedComponent(reduxList, options, state$, dispatch, props$);
};

/**
 * @param WrappedComponent
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 */
export const ReduxListStreamedContainer = (WrappedComponent, reduxList, options) => {
  const container = StreamedComponent(reduxList, options);
  const fuseContainer = fuse(container)(WrappedComponent);

  const propTypes = {};
  defaults(propTypes, WrappedComponent.propTypes || {}, listPropsTypes);
  WrappedComponent.propTypes = propTypes;

  const defaultProps = {};
  defaults(defaultProps, WrappedComponent.defaultProps || {}, listDefaultProps);
  WrappedComponent.defaultProps = defaultProps;

  fuseContainer.defaultProps = {
    init: defaultProps.init,
  };

  fuseContainer.displayName = 'ReduxListStreamedContainer';

  return fuseContainer;
};

/**
 * @param {Observable} state$
 * @param {Observable} props$
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @return {Observable<ReduxAction>}
 */
function initData(state$, props$, reduxList, options) {
  /** @type {ReduxObservableListActions} */
  const actions = reduxList.actions;
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  if (options.autoInit && reduxListOptions.hasParentId) {
    const parentId$ = props$
      .distinctUntilChanged(null, props => props.parentId);

    return Observable
      .merge(
        // first id
        parentId$
          .take(1)
          .switchMap(props => updateChildList(state$, reduxList, options, null, props)),

        // successive IDs
        parentId$
          .pairwise()
          .switchMap(([prevProps, nextProps]) => updateChildList(state$, reduxList, options, prevProps, nextProps)),
      );
  } else if (options.autoInit) {
    return Observable.of(actions.initList());
  }
}

/**
 * @param {Observable} state$
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @param {?Object} prevProps
 * @param {Object} nextProps
 * @return {Observable<ReduxAction>}
 */
function updateChildList(state$, reduxList, options, prevProps, nextProps) {
  /** @type {ReduxObservableListActions} */
  const actions = reduxList.actions;
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  const prevId = prevProps ? prevProps.parentId : null;
  const nextId = nextProps.parentId;

  return state$
    .take(1)
    .mergeMap(rootState => {
      const state = reduxListOptions.getState(rootState);

      if (!reduxListOptions.hasParentId) {
        return state.init
          ? Observable.empty()
          : Observable.of(actions.initList(getInitActionPayload(reduxListOptions, options, nextProps)));

      } else {
        if (state.init && state.parentId === nextId) {
          return Observable.empty();
        } else if (!state.init) {
          return Observable.of(actions.initList(getInitActionPayload(reduxListOptions, options, nextProps)));
        } else if (state.init && state.parentId === prevId) {
          return Observable.merge(
            Observable.of(actions.destroyList({}, {parentId: prevId})),
            Observable
              .timer(33)
              .take(1)
              .mapTo(actions.initList(getInitActionPayload(reduxListOptions, options, nextProps))),
          );
        } else {
          // or empty
          return Observable.merge(
            Observable.of(actions.destroyList({}, {parentId: state.parentId})),
            Observable
              .timer(33)
              .take(1)
              .mapTo(actions.initList(getInitActionPayload(reduxListOptions, options, nextProps))),
          );
        }
      }
    });
}

/**
 *
 * @param {Observable<Object>} props$
 * @param {ReduxObservableListOptions} reduxListOptions
 * @return {Observable<Object>}
 */
function buildParentId$(props$, reduxListOptions) {
  return !reduxListOptions.hasParentId
    ? Observable.of(void 0)
    : props$
      .map(props => props.parentId)
      .distinctUntilChanged();
}

function stateProp$(prop, state$, reduxListOptions) {
  return state$
    .map(reduxListOptions.getState)
    .map(state => state[prop])
    .distinctUntilChanged();
}

/**
 * Transform list from redux state into an array.
 * Applies options.listTransform if defined.
 *
 * @param {Observable} state$
 * @param {Observable} props$
 * @param {ReduxList} reduxList
 * @param {ReduxListComponentOptions} options
 * @return {Observable<Array>} transformed array of items from redux store
 */
export function reduxList$(state$, props$, reduxList, options) {
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  return state$
    .map(reduxListOptions.getState)
    .distinctUntilChanged(compareState)
    .map(state => options.listTransform(reduxListOptions.listToArray(reduxListOptions, state)));
}

function compareListStateMeta(a, b) {
  if (a === b || (!a && !b)) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return a.list === b.list
      && a.isFiltered === b.isFiltered
      && a.sortOptions === b.sortOptions
      && a.hasMore === b.hasMore
      && a.mode === b.mode
      && a.total === b.total;
  }
}

function compareState(a, b) {
  if (a === b || (!a && !b)) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return a.list === b.list && a.isFiltered === b.isFiltered && a.sortOptions === b.sortOptions;
  }
}

export function getInitActionPayload(reduxListOptions, options, props) {
  if (options.useInitFilter) {
    const payload = {
      parentId: props.parentId,
    };

    if (reduxListOptions.useAutoFilter && has(props, 'filter'))
      payload.filter = props.filter;
    if (reduxListOptions.useFilterOptions && has(props, 'filterOptions'))
      payload.filterOptions = props.filterOptions;
    if (reduxListOptions.useSortOptions && has(props, 'sortOptions'))
      payload.sortOptions = props.sortOptions;

    return payload;

  } else {
    return {
      parentId: props.parentId,
    };
  }
}

/**
 * @param {Observable} state$
 * @param {ReduxObservableListOptions} reduxListOptions
 */
export function buildFilterData$(state$, reduxListOptions) {
  const filter$ = !reduxListOptions.useAutoFilter
    ? Observable.of(null)
    : state$
      .map(state => reduxListOptions.getState(state).filter)
      .distinctUntilChanged();
  const filterOptions$ = !reduxListOptions.useFilterOptions
    ? Observable.of(null)
    : state$
      .map(state => reduxListOptions.getState(state).filterOptions)
      .distinctUntilChanged();
  const sortOptions$ = !reduxListOptions.useSortOptions
    ? Observable.of(null)
    : state$
      .map(state => reduxListOptions.getState(state).sortOptions)
      .distinctUntilChanged();

  return Observable
    .combineLatest(
      filter$,
      filterOptions$,
      sortOptions$,
      (filter, filterOptions, sortOptions) => {
        const filterData = {};

        if (reduxListOptions.useAutoFilter)
          filterData.filter = filter;

        if (reduxListOptions.useFilterOptions)
          filterData.filterOptions = filterOptions;

        if (reduxListOptions.useSortOptions)
          filterData.sortOptions = sortOptions;

        return filterData;
      }
    );
}

/**
 * @param {Observable} state$
 * @param {ReduxListComponentOptions} options
 * @param {ReduxObservableListOptions} reduxListOptions
 */
export function buildFilterModalOptions$(state$, options, reduxListOptions) {
  if (!options.searchOnly) {
    return Observable
      .combineLatest(
        state$
          .map(state => reduxListOptions.getState(state).filterOptions)
          .distinctUntilChanged(),
        state$
          .map(state => reduxListOptions.getState(state).sortOptions)
          .distinctUntilChanged(),
        (filterOptions, sortOptions) => options.filterModalOptions.getOptions(filterOptions, sortOptions)
      );
  } else {
    return Observable.of(null);
  }
}

/**
 * @param {Observable} state$
 * @param {ReduxListComponentOptions} options
 * @param {ReduxObservableListOptions} reduxListOptions
 */
export function buildHasMore$(state$, options, reduxListOptions) {
  return Observable
    .combineLatest(
      state$
        .map(state => reduxListOptions.getState(state).isFiltered)
        .distinctUntilChanged(),
      state$
        .map(state => reduxListOptions.getState(state).filteredHasMore)
        .distinctUntilChanged(),
      state$
        .map(state => reduxListOptions.getState(state).hasMore)
        .distinctUntilChanged(),
      (isFiltered, filteredHasMore, hasMore) => hasMoreFromState(isFiltered, filteredHasMore, hasMore)
    );
}

/**
 * @param {Observable} state$
 * @param {ReduxListComponentOptions} options
 * @param {ReduxObservableListOptions} reduxListOptions
 */
export function buildTotalProps$(state$, options, reduxListOptions) {
  return Observable
    .combineLatest(
      state$
        .map(state => reduxListOptions.getState(state).mode)
        .distinctUntilChanged(),
      state$
        .map(state => reduxListOptions.getState(state).isFiltered)
        .distinctUntilChanged(),
      state$
        .map(state => reduxListOptions.getState(state).total)
        .distinctUntilChanged(),
      state$
        .map(state => reduxListOptions.getState(state).filteredTotal)
        .distinctUntilChanged(),
      (mode, isFiltered, total, filteredTotal) => ({mode, isFiltered, total, filteredTotal})
    );
}

/**
 * @param {ReduxObservableListActions} actions
 * @param {ReduxObservableListOptions} reduxListOptions
 * @param {Function} dispatch
 * @param {Observable} props$
 * @param {Observable} filterData$
 * @return {Function}
 */
export function createAutoFilterEvent(actions, reduxListOptions, dispatch, props$, filterData$) {
  if (reduxListOptions.useAutoFilter) {
    const {
      handler: onFilterChange,
      stream: filterChange$,
    } = createEventHandler();

    filterChange$
      .withLatestFrom(props$, filterData$)
      .takeUntil(props$.last())
      .subscribe(([filter, props, filterData]) => {
        if (filterData.filter !== filter) {
          dispatch(actions.applyFilter({...filterData, filter}, {parentId: props.parentId}));
        }
      });

    return onFilterChange;
  } else {
    return noop;
  }
}

/**
 * @param {ReduxObservableListActions} actions
 * @param {ReduxObservableListOptions} reduxListOptions
 * @param {Function} dispatch
 * @param {Observable} props$
 * @param {Observable} filterData$
 * @return {Function}
 */
function createFilterOptionsEvent(actions, reduxListOptions, dispatch, props$, filterData$) {
  if (reduxListOptions.useFilterOptions) {
    const {
      handler: onFilterOptionsUpdate,
      stream: filterOptionsUpdate$,
    } = createEventHandler();

    filterOptionsUpdate$
      .withLatestFrom(props$, filterData$)
      .takeUntil(props$.last())
      .subscribe(([filterOptions, props, filterData]) => {
        if (filterData.filterOptions !== filterOptions) {
          dispatch(actions.applyFilter({...filterData, filterOptions}, {parentId: props.parentId}));
        }
      });

    return onFilterOptionsUpdate;
  } else {
    return noop;
  }
}

/**
 * @param {ReduxObservableListActions} actions
 * @param {ReduxObservableListOptions} reduxListOptions
 * @param {Function} dispatch
 * @param {Observable} props$
 * @param {Observable} filterData$
 * @return {Function}
 */
function createSortOptionsEvent(actions, reduxListOptions, dispatch, props$, filterData$) {
  if (reduxListOptions.useSortOptions) {
    const {
      handler: onSortOptionsUpdate,
      stream: sortOptionsUpdate$,
    } = createEventHandler();

    sortOptionsUpdate$
      .withLatestFrom(props$, filterData$)
      .takeUntil(props$.last())
      .subscribe(([sortOptions, props, filterData]) => {
        if (filterData.sortOptions !== sortOptions) {
          dispatch(actions.applyFilter({...filterData, sortOptions}, {parentId: props.parentId}));
        }
      });

    return onSortOptionsUpdate;
  } else {
    return noop;
  }
}

/**
 * @param {Boolean} isFiltered
 * @param {Boolean} filteredHasMore
 * @param {Boolean} hasMore
 * @return {boolean}
 */
function hasMoreFromState(isFiltered, filteredHasMore, hasMore) {
  return (isFiltered && filteredHasMore) || (!isFiltered && hasMore);
}

/**
 * @param {ReduxObservableListOptions} reduxListOptions
 * @param {ReduxListComponentOptions} [o]
 * @return {ReduxListComponentOptions}
 */
export function applyDefaultReduxListComponentOptions(reduxListOptions, o) {
  /** @type {ReduxListComponentOptions} options **/
  const options = {};
  defaults(options, o || {}, {
    excludeFilterModalOptions: false,
    listTransform,
    autoInit: true,
    useInitFilter: false,
  });

  options.searchOnly = options.excludeFilterModalOptions
    ? true
    : !reduxListOptions.useFilterOptions && !reduxListOptions.useSortOptions;

  // Validate options
  if (!options.searchOnly && !isObject(options.filterModalOptions)) {
    throw new Error('ReduxListComponentOptions missing required filterModalOptions');
  }
  if (!isFunction(options.listTransform)) {
    throw new Error('ReduxListComponentOptions listTransform must be a function');
  }

  return options;
}

function listTransform(list) {
  return list;
}

function showPlaceholder(list, mode) {
  return mode === ViewMode.SUCCESS && (!list || list.length === 0);
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/redux/ReduxListContainer.js